import { useState, useCallback } from 'react';
import myDriveService, { DirCategory, DriveFile } from '../services/myDriveService';
import { useMessage } from '../../../../contexts/MessageContext';

export function useDriveFiles(dir_category: DirCategory) {
  const { showApiError } = useMessage();
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (prefix = '') => {
    setLoading(true);
    try {
      const data = await myDriveService.listFiles(dir_category, prefix);
      setFiles(data.items);
      setTotal(data.total);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [dir_category, showApiError]);

  const remove = useCallback(async (file_id: number, recursive = false) => {
    try {
      if (recursive) {
        await myDriveService.deleteFileRecursive(file_id);
      } else {
        await myDriveService.deleteFile(file_id);
      }
      setFiles(prev => prev.filter(f => f.id !== file_id));
      setTotal(prev => prev - 1);
    } catch (err) {
      showApiError(err);
    }
  }, [showApiError]);

  const createFolder = useCallback(async (relative_path: string) => {
    try {
      const folder = await myDriveService.createFolder(dir_category, relative_path);
      setFiles(prev => [...prev, folder].sort((a, b) => a.relative_path.localeCompare(b.relative_path)));
      setTotal(prev => prev + 1);
      return folder;
    } catch (err) {
      showApiError(err);
      return null;
    }
  }, [dir_category, showApiError]);

  const rename = useCallback(async (file_id: number, new_filename: string) => {
    try {
      const updated = await myDriveService.renameFile(file_id, new_filename);
      setFiles(prev => prev.map(f => f.id === file_id ? updated : f));
      return updated;
    } catch (err) {
      showApiError(err);
      return null;
    }
  }, [showApiError]);

  const move = useCallback(async (file_id: number, new_relative_path: string) => {
    try {
      const updated = await myDriveService.moveFile(file_id, new_relative_path);
      setFiles(prev => prev.map(f => f.id === file_id ? updated : f));
      return updated;
    } catch (err) {
      showApiError(err);
      return null;
    }
  }, [showApiError]);

  return { files, total, loading, load, remove, createFolder, rename, move };
}
